import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { UploadSimple, Sparkle, CheckCircle, XCircle, Receipt, Question, ArrowClockwise, X } from "@phosphor-icons/react";

/**
 * ReceiptInbox — review-before-lodge staging area for imported receipts.
 *
 * Flow:
 *  1. Drop a WhatsApp .zip → backend parses chat.txt, stages every image
 *     with a best-guess address from adjacent messages, no writes to jobs
 *  2. Admin sees a table — every row has thumbnail, tech, timestamp,
 *     guessed address, vendor, amount, and a checkbox
 *  3. "Analyze with AI" fires Claude Vision on the selected rows to fill
 *     vendor + total
 *  4. Admin ticks matched rows, hits "Lodge selected N" → those receipts
 *     are pushed onto the matched jobs' `receipts` array
 *  5. Rows without a match stay in the inbox forever (never scattered)
 */
export default function ReceiptInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("staged");
  const [uploading, setUploading] = useState(false);
  const [analyzingIds, setAnalyzingIds] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/receipts/inbox", { params: { status } });
      setRows(r.data.receipts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a WhatsApp .zip export");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/admin/receipts/import-whatsapp", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,  // 5 min for large exports
      });
      const { staged, duplicates_skipped } = r.data;
      toast.success(`✓ Staged ${staged} receipts${duplicates_skipped ? ` · ${duplicates_skipped} dup skipped` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const analyzeRow = async (id) => {
    setAnalyzingIds(prev => new Set([...prev, id]));
    try {
      const r = await api.post(`/admin/receipts/${id}/ai-analyze`);
      setRows(prev => prev.map(row => row.id === id ? { ...row, ...r.data } : row));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally {
      setAnalyzingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const analyzeSelected = async () => {
    const ids = Array.from(selected).filter(id => {
      const row = rows.find(r => r.id === id);
      return row && !row.vendor && !row.ai_analysis;
    });
    if (!ids.length) { toast("Nothing to analyze — already done"); return; }
    if (!window.confirm(`Run Claude Vision on ${ids.length} receipt${ids.length === 1 ? "" : "s"}? Costs ~$${(ids.length * 0.006).toFixed(2)}.`)) return;
    for (const id of ids) await analyzeRow(id);
    toast.success(`✓ Analyzed ${ids.length} receipt${ids.length === 1 ? "" : "s"}`);
  };

  const rejectRow = async (id) => {
    if (!window.confirm("Reject this receipt? Won't be lodged and stays in the rejected list.")) return;
    try {
      await api.post(`/admin/receipts/${id}/reject`);
      setRows(prev => prev.filter(r => r.id !== id));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      toast.success("Receipt rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  const commitSelected = async () => {
    const withMatch = Array.from(selected).filter(id => {
      const r = rows.find(x => x.id === id);
      return r && r.matched_job_id;
    });
    const missing = selected.size - withMatch.length;
    if (!withMatch.length) {
      toast.error("None of the selected rows have a matched job. Pick a job first.");
      return;
    }
    let msg = `Lodge ${withMatch.length} receipt${withMatch.length === 1 ? "" : "s"} to their matched WOs?`;
    if (missing > 0) msg += `\n\n(${missing} unmatched row${missing === 1 ? "" : "s"} will be skipped and stay in the inbox.)`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/receipts/commit", { receipt_ids: withMatch });
      toast.success(`✓ Lodged ${r.data.lodged} receipt${r.data.lodged === 1 ? "" : "s"}`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const fmtMoney = (cents) => cents ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
  const totalToCommit = Array.from(selected).reduce((s, id) => {
    const r = rows.find(x => x.id === id);
    return s + (r?.amount_cents || 0);
  }, 0);

  return (
    <AdminShell>
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Accounting</div>
          <h1 className="font-display font-black text-2xl text-[#0f172a]">Receipt Inbox</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Drop a WhatsApp chat export. Every receipt gets staged for review — nothing lands on a WO
            until you tick it and hit <b>Lodge selected</b>. Unmatched receipts stay here forever, never scattered.
          </p>
        </div>

        {/* Upload + filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <label className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl font-black text-xs uppercase tracking-widest cursor-pointer transition-colors ${uploading ? "bg-slate-400 text-white cursor-wait" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`} data-testid="receipt-upload-label">
            <input ref={fileInputRef} type="file" accept=".zip"
              onChange={onUpload} disabled={uploading} className="hidden"/>
            <UploadSimple weight="bold" size={16}/>
            {uploading ? "Uploading & parsing…" : "Import WhatsApp .zip"}
          </label>

          <div className="ml-auto inline-flex rounded-xl border-2 border-slate-200 overflow-hidden">
            {["staged", "committed", "rejected", "all"].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                data-testid={`receipt-tab-${s}`}
                className={`px-3 h-9 text-[10px] font-black uppercase tracking-widest ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}>
                {s}
              </button>
            ))}
          </div>
          <button onClick={load} className="h-9 w-9 rounded-lg bg-white border-2 border-slate-200 flex items-center justify-center hover:bg-slate-50"
            data-testid="receipt-refresh">
            <ArrowClockwise size={14} weight="bold"/>
          </button>
        </div>

        {/* Action bar — only when rows are selected */}
        {selected.size > 0 && (
          <div className="mb-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl flex flex-wrap items-center gap-2" data-testid="receipt-action-bar">
            <div className="text-sm font-black text-amber-900">
              {selected.size} selected · {fmtMoney(totalToCommit)}
            </div>
            <button onClick={analyzeSelected} disabled={busy}
              data-testid="receipt-analyze-btn"
              className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white text-[10px] font-black uppercase tracking-widest rounded-lg inline-flex items-center gap-1 disabled:opacity-50">
              <Sparkle size={12} weight="fill"/> Analyze w/ AI
            </button>
            <button onClick={commitSelected} disabled={busy}
              data-testid="receipt-commit-btn"
              className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest rounded-lg inline-flex items-center gap-1 disabled:opacity-50">
              <CheckCircle size={12} weight="fill"/> Lodge Selected
            </button>
            <button onClick={() => setSelected(new Set())}
              className="ml-auto text-[10px] text-amber-800 font-black uppercase tracking-widest hover:text-amber-900">
              Clear
            </button>
          </div>
        )}

        {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="p-10 text-center bg-white border-2 border-slate-200 rounded-xl" data-testid="receipt-empty">
            <Receipt weight="fill" size={40} className="mx-auto text-slate-300 mb-2"/>
            <div className="font-display font-black text-lg text-slate-700">
              {status === "staged" ? "Nothing to review" : `No ${status} receipts`}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {status === "staged" ? "Drop a WhatsApp .zip to get started." : "Switch tabs to see other states."}
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden" data-testid="receipt-table">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-500">
              <div className="col-span-1 text-center">
                {status === "staged" && (
                  <input type="checkbox" onChange={selectAll}
                    checked={rows.length > 0 && selected.size === rows.length}
                    data-testid="receipt-select-all" className="w-4 h-4 cursor-pointer"/>
                )}
              </div>
              <div className="col-span-1">Photo</div>
              <div className="col-span-1">Tech</div>
              <div className="col-span-1">When</div>
              <div className="col-span-3">Match to Job</div>
              <div className="col-span-2">Vendor</div>
              <div className="col-span-2">Total</div>
              <div className="col-span-1 text-right"></div>
            </div>
            {rows.map(r => (
              <ReceiptRow key={r.id} row={r}
                selected={selected.has(r.id)}
                onToggle={() => toggleSelect(r.id)}
                onAnalyze={() => analyzeRow(r.id)}
                analyzing={analyzingIds.has(r.id)}
                onReject={() => rejectRow(r.id)}
                onUpdate={(patch) => setRows(prev => prev.map(x => x.id === r.id ? { ...x, ...patch } : x))}
                canEdit={status === "staged"}
              />
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function ReceiptRow({ row, selected, onToggle, onAnalyze, analyzing, onReject, onUpdate, canEdit }) {
  const [addrQ, setAddrQ] = useState(row.guessed_address || "");
  const [addrResults, setAddrResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [saving, setSaving] = useState(false);

  const searchJobs = async (q) => {
    setAddrQ(q);
    if (!q || q.length < 2) { setAddrResults([]); return; }
    try {
      const r = await api.get("/admin/receipts/suggest-jobs", { params: { q } });
      setAddrResults(r.data.jobs || []);
      setShowResults(true);
    } catch { setAddrResults([]); }
  };
  const pickJob = async (j) => {
    setShowResults(false);
    setSaving(true);
    try {
      await api.patch(`/admin/receipts/${row.id}`, {
        matched_job_id: j.id,
        guessed_address: `${j.property_address}${j.unit ? " Unit " + j.unit : ""}`,
      });
      onUpdate({ matched_job_id: j.id, guessed_address: `${j.property_address}${j.unit ? " Unit " + j.unit : ""}` });
      toast.success(`Matched → WO ${j.work_order_number || j.id.slice(-4)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match failed");
    } finally { setSaving(false); }
  };
  const clearMatch = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/receipts/${row.id}`, { matched_job_id: null });
      onUpdate({ matched_job_id: null });
    } finally { setSaving(false); }
  };
  const editField = async (field, value) => {
    try {
      await api.patch(`/admin/receipts/${row.id}`, { [field]: value });
      onUpdate({ [field]: value });
    } catch { toast.error("Save failed"); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  const thumbUrl = row.storage_path
    ? `${backend}/api/media/${encodeURIComponent(row.storage_path)}?auth=${token}`
    : null;

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm hover:bg-slate-50 relative" data-testid={`receipt-row-${row.id}`}>
      <div className="col-span-1 text-center">
        {canEdit && (
          <input type="checkbox" checked={selected} onChange={onToggle}
            data-testid={`receipt-row-check-${row.id}`}
            className="w-4 h-4 cursor-pointer"/>
        )}
      </div>
      <div className="col-span-1">
        {thumbUrl && (
          <a href={thumbUrl} target="_blank" rel="noreferrer">
            <img src={thumbUrl} alt="" className="w-14 h-14 object-cover rounded border border-slate-200 hover:border-cyan-500"/>
          </a>
        )}
      </div>
      <div className="col-span-1 text-xs">
        <div className="font-semibold truncate">{row.sent_by_name?.split(" ")[0] || "—"}</div>
      </div>
      <div className="col-span-1 text-xs text-slate-600 font-mono">
        {row.sent_at ? new Date(row.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
      </div>
      <div className="col-span-3 relative">
        {row.matched_job_id ? (
          <div className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-black">
            <CheckCircle size={12} weight="fill"/>
            <span className="truncate max-w-[200px]">{row.guessed_address || "matched"}</span>
            {canEdit && (
              <button onClick={clearMatch} className="ml-1 text-emerald-700 hover:text-red-600"><X size={10} weight="bold"/></button>
            )}
          </div>
        ) : canEdit ? (
          <>
            <input type="text" value={addrQ} onChange={e => searchJobs(e.target.value)}
              onFocus={() => setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              data-testid={`receipt-addr-input-${row.id}`}
              placeholder="Type address or WO#…"
              className="w-full h-8 px-2 border border-slate-300 rounded text-xs outline-none focus:border-cyan-500"/>
            {showResults && addrResults.length > 0 && (
              <div className="absolute z-20 top-9 left-0 right-0 bg-white border-2 border-slate-200 rounded shadow-lg max-h-52 overflow-y-auto">
                {addrResults.map(j => (
                  <button key={j.id} onClick={() => pickJob(j)}
                    data-testid={`receipt-addr-pick-${row.id}-${j.id}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-cyan-50 border-b border-slate-100 text-xs">
                    <div className="font-black text-cyan-800">WO {j.work_order_number || j.id.slice(-4)}</div>
                    <div className="text-slate-700 truncate">{j.property_address}{j.unit ? ` · Unit ${j.unit}` : ""}</div>
                    <div className="text-[9px] text-slate-500 uppercase">{j.status}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className="text-xs text-slate-500 truncate block">{row.guessed_address || "—"}</span>
        )}
      </div>
      <div className="col-span-2">
        {canEdit ? (
          <input type="text" value={row.vendor || ""}
            onBlur={e => editField("vendor", e.target.value)}
            onChange={e => onUpdate({ vendor: e.target.value })}
            data-testid={`receipt-vendor-${row.id}`}
            placeholder="Lowe's, HD, …"
            className="w-full h-8 px-2 border border-slate-200 rounded text-xs outline-none focus:border-cyan-500"/>
        ) : (
          <span className="text-xs">{row.vendor || "—"}</span>
        )}
      </div>
      <div className="col-span-2">
        {canEdit ? (
          <input type="number" step="0.01"
            value={row.amount_cents ? (row.amount_cents / 100).toFixed(2) : ""}
            onBlur={e => editField("amount_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
            onChange={e => onUpdate({ amount_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
            data-testid={`receipt-amt-${row.id}`}
            placeholder="0.00"
            className="w-full h-8 px-2 border border-slate-200 rounded text-xs outline-none focus:border-cyan-500 font-mono"/>
        ) : (
          <span className="text-xs font-mono">${row.amount_cents ? (row.amount_cents / 100).toFixed(2) : "0.00"}</span>
        )}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-1">
        {canEdit && !row.ai_analysis && !row.vendor && (
          <button onClick={onAnalyze} disabled={analyzing}
            data-testid={`receipt-ai-${row.id}`}
            title="Run Claude Vision to extract vendor + total"
            className="h-7 px-2 bg-cyan-100 text-cyan-700 hover:bg-cyan-200 rounded text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50">
            <Sparkle size={10} weight="fill"/> {analyzing ? "…" : "AI"}
          </button>
        )}
        {canEdit && (
          <button onClick={onReject}
            data-testid={`receipt-reject-${row.id}`}
            title="Reject this receipt"
            className="h-7 w-7 flex items-center justify-center hover:bg-red-100 text-slate-400 hover:text-red-600 rounded">
            <XCircle size={14} weight="fill"/>
          </button>
        )}
      </div>
    </div>
  );
}
